import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface SkeletonCardProps {
  className?: string;
  showHeader?: boolean;
  lines?: number;
}

/**
 * Reusable skeleton card for loading states
 */
export function SkeletonCard({ 
  className, 
  showHeader = true,
  lines = 3 
}: SkeletonCardProps) {
  return (
    <Card className={cn("w-full", className)}>
      {showHeader && (
        <CardHeader>
          <Skeleton className="h-6 w-32 mb-2" />
          <Skeleton className="h-4 w-48" />
        </CardHeader>
      )}
      <CardContent className="space-y-3">
        {Array.from({ length: lines }).map((_, i) => (
          <Skeleton key={i} className="h-4 w-full" />
        ))}
      </CardContent>
    </Card>
  );
}

/**
 * Skeleton for dashboard metric cards
 */
export function SkeletonMetricCard({ className }: { className?: string }) {
  return (
    <Card className={cn("w-full", className)}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-5 w-5 rounded-full" />
        </div>
        <Skeleton className="h-8 w-32 mb-2" />
        <Skeleton className="h-3 w-20" />
      </CardContent>
    </Card>
  );
}

/**
 * Skeleton for table rows
 */
export function SkeletonTableRow({ columns = 5 }: { columns?: number }) {
  return (
    <tr className="border-b">
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} className="p-4">
          <Skeleton className="h-4 w-full" />
        </td>
      ))}
    </tr>
  );
}

/**
 * Skeleton for chart loading
 */
export function SkeletonChart({ 
  height = 400,
  className 
}: { 
  height?: number;
  className?: string;
}) {
  return (
    <div 
      className={cn("w-full rounded-lg bg-muted animate-pulse", className)}
      style={{ height }}
    >
      <div className="h-full flex items-end justify-around p-8 gap-2">
        {Array.from({ length: 12 }).map((_, i) => (
          <div
            key={i}
            className="bg-muted-foreground/20 rounded-t"
            style={{
              height: `${30 + Math.random() * 70}%`,
              flex: 1,
            }}
          />
        ))}
      </div>
    </div>
  );
}
